import CloseIcon from '@mui/icons-material/Close';
import { Box } from '@mui/material';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Modal from '@mui/material/Modal';
import classNames from 'classnames';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ModalTechnology from '/@/components/crop-monitoring/modal-technology';
import { _ } from '/@/locale';

import { reverse } from '/@/utils/router';
import colors from '../../theme-provider/colors';

import styles from './styles.module.css';

const CPLinesDesktop = ({ steps, activeStep, height }) => {
    return (
        <Box
            sx={{
                display: 'flex',
                position: 'absolute',
                width: '100%',
                height: `${height}px`,
                top: '-10px',
                pointerEvents: 'none',
            }}
        >
            {steps.map((step, index) => (
                <Fragment key={index}>
                    <Box
                        sx={[
                            {
                                display: 'flex',
                                width: '0%',
                                height: '100%',
                                zIndex: -1,
                            },
                            !step.empty && {
                                '&::after': {
                                    content: '""',
                                    borderLeft: `1px solid ${colors.cropMonitoring.grayLine}`,
                                    marginLeft: '-0.5px',
                                },
                            },
                            index + 1 === activeStep && {
                                zIndex: 2,
                                '&::after': {
                                    content: '""',
                                    borderLeft: (t) => `2px solid ${t.palette.warning.main}`,
                                    marginLeft: '-1px',
                                },
                            },
                            index === steps.length - 1 && {
                                width: '0px',
                            },
                        ]}
                    />
                    <Box
                        sx={[
                            {
                                display: 'flex',
                                width: '100%',
                                height: '100%',
                                zIndex: 1,
                            },
                            index === steps.length - 1 && {
                                width: '0px',
                            },
                            index + 1 >= activeStep && {
                                background: '#ffffff78',
                            },
                        ]}
                    />
                </Fragment>
            ))}
        </Box>
    );
};

const CropProtectionProductDetail = ({ product, open, setOpen, state }) => {
    const history = useNavigate();
    const handleClose = () => {
        setOpen(false);
    };

    const handleClick = () => {
        history(
            reverse('catalog.ProductDetail', {
                params: {
                    type: 'crop_protection',
                    itemId: product.id,
                },
            })
        );
    };

    if (!product) return null;
    return (
        <Modal open={open} onClose={handleClose}>
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 325,
                    bgcolor: '#fff',
                    borderRadius: 2,
                    p: 3,
                }}
            >
                <IconButton onClick={handleClose} sx={{ position: 'absolute', right: '10px', top: '10px' }}>
                    <CloseIcon />
                </IconButton>
                <ModalTechnology
                    image={product.image}
                    pills={{ type: product.type, crops: product.crops }}
                    name={product.name}
                    description={product.shortDescription}
                    btnText={_('Ver más')}
                    state={state}
                    click={handleClick}
                />
            </Box>
        </Modal>
    );
};

const ProgressBarTableDesktop = ({
    steps,
    activeStep,
    productsData,
    stageNames,
    modalOpened,
    modalProduct,
    modalState,
    setModalOpened,
    openModal,
}) => {
    const CPTableEl = useRef(null);
    const [CPTableHeight, setCPTableHeight] = useState(0);

    useEffect(() => {
        if (CPTableEl.current) {
            setCPTableHeight(CPTableEl.current.clientHeight + 10);
        }
    }, [productsData]);

    return (
        <Box
            sx={{
                mt: 2,
                mb: 8,
                mx: '42px',
                position: 'relative',
                width: `${(steps.length - 1) * 95.5}px`,
            }}
        >
            <CPLinesDesktop steps={steps} activeStep={activeStep} height={CPTableHeight} />
            <Box ref={CPTableEl}>
                {productsData.map((row, rowIndex) => (
                    <Grid container direction='row' columns={steps.length - 1} sx={{ my: 1 }} key={rowIndex}>
                        {row
                            .reduce((row, col, index) => {
                                let lastItem;
                                if (row.length > 0) {
                                    lastItem = row[row.length - 1];
                                }

                                if (lastItem && col.product.id === lastItem.product.id) {
                                    lastItem.cols = lastItem.cols + 1;
                                    lastItem.lastStage = index;
                                } else {
                                    row.push({
                                        cols: 1,
                                        startStage: index,
                                        product: col.product,
                                    });
                                }

                                return row;
                            }, [])
                            .map(({ cols, product, startStage, lastStage }, i) => {
                                if (product.id === undefined)
                                    return <Grid item xs={cols} sx={{ px: 0.25 }} key={`empty-${rowIndex}-${i}`} />;
                                return (
                                    <Grid
                                        item
                                        xs={cols}
                                        sx={{ px: 0.25, pr: 2 }}
                                        key={`${rowIndex}-${product.id}-${startStage}`}
                                        onClick={() => openModal(product, startStage, cols)}
                                    >
                                        <Box
                                            sx={[
                                                {
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    height: '66px',
                                                    borderRadius: '6px',
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        boxShadow: 'inset #ffffff57 0px 0px 0px 4px',
                                                    },
                                                },
                                                cols == 1 && { justifyContent: 'center' },
                                            ]}
                                            className={classNames(
                                                styles['type-default'],
                                                styles[`type-${product.type.en.toLowerCase()}`]
                                            )}
                                        >
                                            <Box
                                                sx={[
                                                    {
                                                        display: 'flex',
                                                        alignContent: 'center',
                                                        justifyContent: 'center',
                                                        height: { sm: '75%', md: '80%', lg: '90%' },
                                                        width: `${100 / cols}%`,
                                                        p: 1,
                                                    },
                                                    cols > 1 && { pr: 1.25 },
                                                ]}
                                            >
                                                <img
                                                    style={{ maxHeight: '100%' }}
                                                    src={product.image}
                                                    alt={product.name}
                                                />
                                            </Box>

                                            {cols > 1 && (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        color: 'white',
                                                    }}
                                                >
                                                    <Box sx={{ fontWeight: 'bold', fontSize: '0.8em' }}>
                                                        {product.name}
                                                    </Box>

                                                    <Box sx={{ fontSize: { xs: '0.75em', lg: '0.8em' } }}>
                                                        {stageNames[startStage]}-{stageNames[lastStage]}
                                                    </Box>
                                                </Box>
                                            )}
                                        </Box>
                                    </Grid>
                                );
                            })}
                    </Grid>
                ))}
            </Box>
            <CropProtectionProductDetail
                open={modalOpened}
                setOpen={setModalOpened}
                product={modalProduct}
                state={modalState}
            />
        </Box>
    );
};

export default ProgressBarTableDesktop;
